import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search } from "lucide-react";
import { Deck, LANG_FLAGS } from "@/types/linguadeck";
import { addDecksToFolder } from "@/lib/db";
import { inspectQueryError } from "@/diagnostics";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";

interface AddMaterialsModalProps {
  folderId: string;
  decks: Deck[];
  existingDeckIds: string[];
  onClose: () => void;
  onSaved: () => void;
}

export default function AddMaterialsModal({
  folderId,
  decks,
  existingDeckIds,
  onClose,
  onSaved,
}: AddMaterialsModalProps) {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visibleDecks = useMemo(
    () =>
      [...decks]
        .filter((deck) => !deck.id.startsWith("deck_demo"))
        .sort((a, b) => {
          const aTime = a.created_at ? Date.parse(a.created_at) : 0;
          const bTime = b.created_at ? Date.parse(b.created_at) : 0;
          if (aTime !== bTime) return bTime - aTime;
          return a.title.localeCompare(b.title);
        }),
    [decks]
  );

  // Exclude demo decks (don't exist in Supabase) and decks already in folder
  const decksNotInFolder = visibleDecks.filter(
    (d) => !existingDeckIds.includes(d.id)
  );

  // Search filter: title + description, case-insensitive, partial match
  const matchesSearch = (deck: Deck, q: string) => {
    if (!q.trim()) return true;
    const lower = q.trim().toLowerCase();
    const titleMatch = deck.title?.toLowerCase().includes(lower) ?? false;
    const descMatch = (deck.description ?? "").toLowerCase().includes(lower);
    return titleMatch || descMatch;
  };

  const filteredVisibleDecks = useMemo(
    () =>
      searchQuery.trim()
        ? visibleDecks.filter((d) => matchesSearch(d, searchQuery))
        : visibleDecks,
    [visibleDecks, searchQuery]
  );

  const filteredDecksNotInFolder = filteredVisibleDecks.filter(
    (d) => !existingDeckIds.includes(d.id)
  );
  const availableIds = filteredDecksNotInFolder.map((d) => d.id);
  const allSelected = availableIds.length > 0 && availableIds.every((id) => selectedIds.has(id));

  const toggleDeck = (deckId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(deckId)) next.delete(deckId);
      else next.add(deckId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        availableIds.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        availableIds.forEach((id) => next.add(id));
        return next;
      });
    }
  };

  const handleSave = async () => {
    if (selectedIds.size === 0) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    // Only pass valid deck IDs (exclude demo decks that don't exist in Supabase)
    const idsToAdd = Array.from(selectedIds).filter((id) => !id.startsWith("deck_demo"));
    if (idsToAdd.length === 0) {
      setError(t("folder.addMaterialsError"));
      setSaving(false);
      return;
    }
    if (!folderId || folderId.trim() === "") {
      setError(t("folder.addMaterialsError"));
      setSaving(false);
      return;
    }
    try {
      await addDecksToFolder(folderId, idsToAdd);
      toast.success(t("folder.materialsAdded"));
      onSaved();
    } catch (err) {
      inspectQueryError({
        queryName: "addDecksToFolder",
        flow: "folder.addDecksToFolder",
        table: "folder_decks",
        filters: { folderId, deckIds: idsToAdd },
        responseError: err,
      });
      console.error("Failed to add decks to folder:", { folderId, deckIds: idsToAdd, error: err });
      setError(t("folder.addMaterialsError"));
      toast.error(t("folder.addMaterialsError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="max-w-md"
        style={{ maxHeight: "80vh", display: "flex", flexDirection: "column" }}
      >
        <DialogHeader>
          <DialogTitle>{t("folder.addMaterials")}</DialogTitle>
        </DialogHeader>

        <div
          style={{
            flex: 1,
            overflowY: "auto",
            minHeight: 200,
            maxHeight: 400,
            paddingRight: 8,
          }}
        >
          {visibleDecks.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: "hsl(var(--muted-foreground))",
                fontSize: 14,
              }}
            >
              {t('addMaterials.noDecks')}
            </div>
          ) : decksNotInFolder.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: "hsl(var(--muted-foreground))",
                fontSize: 14,
              }}
            >
              {t('addMaterials.allAdded')}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {/* Search */}
              <div
                style={{
                  position: "relative",
                  marginBottom: 8,
                }}
              >
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "hsl(var(--muted-foreground))",
                    pointerEvents: "none",
                  }}
                />
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("addMaterials.searchPlaceholder")}
                  autoComplete="off"
                  inputMode="search"
                  style={{
                    width: "100%",
                    padding: "10px 12px 10px 40px",
                    borderRadius: "var(--radius-sm)",
                    border: "1px solid hsl(var(--border))",
                    background: "hsl(var(--background))",
                    color: "hsl(var(--foreground))",
                    fontSize: 14,
                    outline: "none",
                  }}
                  aria-label={t("addMaterials.searchPlaceholder")}
                />
              </div>

              {/* Select All */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  cursor: "pointer",
                  background: allSelected ? "rgba(109,90,255,.08)" : "transparent",
                  borderBottom: "1px solid hsl(var(--border))",
                  paddingBottom: 8,
                  marginBottom: 8,
                }}
              >
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                />
                <span style={{ fontSize: 14, fontWeight: 600, color: "hsl(var(--foreground))" }}>
                  {t("folder.selectAll")}
                </span>
              </label>

              {searchQuery.trim() && filteredVisibleDecks.length === 0 ? (
                <div
                  style={{
                    padding: 24,
                    textAlign: "center",
                    color: "hsl(var(--muted-foreground))",
                    fontSize: 14,
                  }}
                >
                  {t("addMaterials.noResults")}
                </div>
              ) : (
                filteredVisibleDecks.map((deck) => {
                const alreadyInFolder = existingDeckIds.includes(deck.id);
                const isChecked = selectedIds.has(deck.id);
                return (
                  <label
                    key={deck.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      borderRadius: "var(--radius-sm)",
                      cursor: alreadyInFolder ? "default" : "pointer",
                      background: isChecked ? "rgba(109,90,255,.08)" : "transparent",
                      opacity: alreadyInFolder ? 0.6 : 1,
                    }}
                  >
                    <Checkbox
                      checked={alreadyInFolder || isChecked}
                      disabled={alreadyInFolder}
                      onCheckedChange={() => !alreadyInFolder && toggleDeck(deck.id)}
                    />
                    <span style={{ fontSize: 14 }}>{deck.emoji || "📚"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 500,
                          color: "hsl(var(--foreground))",
                        }}
                      >
                        {deck.title}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "hsl(var(--muted-foreground))",
                        }}
                      >
                        {LANG_FLAGS[deck.lang]} {deck.lang.toUpperCase()} ·{" "}
                        {t("sidebar.cardsCount", { count: deck.cardCount ?? deck.cards?.length ?? 0 })}
                        {alreadyInFolder && ` · ${t('addMaterials.inFolder')}`}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
            </div>
          )}
        </div>

        {error && (
          <div
            style={{
              padding: 8,
              borderRadius: 8,
              background: "rgba(255,90,138,.1)",
              color: "hsl(var(--accent3))",
              fontSize: 13,
            }}
          >
            {error}
          </div>
        )}

        <DialogFooter style={{ marginTop: 16 }}>
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid hsl(var(--border))",
              background: "hsl(var(--bg3))",
              color: "hsl(var(--foreground))",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            {t('addMaterials.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || selectedIds.size === 0}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "none",
              background: selectedIds.size > 0 ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))",
              color: "white",
              fontFamily: "DM Sans, sans-serif",
              fontSize: 13,
              fontWeight: 500,
              cursor: saving ? "wait" : "pointer",
              opacity: selectedIds.size === 0 ? 0.5 : 1,
            }}
          >
            {saving ? t("folder.loading") : `${t("folder.addMaterials")} (${selectedIds.size})`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
